import React, { useEffect, useRef, useMemo, useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { CopyIcon, SearchIcon } from "lucide-react";
import { ReactComponent as AiStars } from "../../assets/svg/ai-creation.svg";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import RequestView from "./RequestView";
import moment from "moment";

interface RequestsTableProps {
  // Entity information
  entityType: "intentGroup" | "applicationAction";
  entityId: number;
  applicationId?: number; // Only needed for applicationAction type
  
  // Pagination and data
  requests: any[];
  requestsTotal: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  
  // Handlers
  onPageChange: (page: number) => void;
  onFetchRequests: (page: number) => void;
  
  // Date filtering (handled by parent)
  startDate?: string;
  endDate?: string;
  
  // API key
  apiKey: ApiKeyOrGetter;
  
  // Optional customization
  showApplicationColumn?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
}

const RequestsTable: React.FC<RequestsTableProps> = ({
  entityType,
  entityId,
  applicationId,
  requests,
  requestsTotal,
  currentPage,
  pageSize,
  loading,
  onPageChange,
  onFetchRequests,
  startDate,
  endDate,
  apiKey,
  showApplicationColumn = true,
  emptyTitle = "No requests found",
  emptyDescription = "Try adjusting your date filters or check back later."
}) => {
  const navigate = useNavigate();
  const requestsByPageRef = useRef<Record<number, any[]>>({});
  const [showOverlay, setShowOverlay] = useState<string | null>(null);

  // Clear cache when date filters change
  useEffect(() => {
    if (startDate || endDate) {
      requestsByPageRef.current = {};
    }
  }, [startDate, endDate]);

  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handlePageChange = (page: number) => {
    onPageChange(page);
    if (!requestsByPageRef.current[page] || startDate || endDate) {
      onFetchRequests(page);
    }
  };

  const handleRowClick = (request: any) => {
    window.open(`/application/${request.application_id}/session/${encodeURIComponent(request.session_id)}/request/${request.request_id}`, "_blank");
  };

  // Define columns for the table
  const columns: PaginatedTableColumn<any>[] = useMemo(() => {
    const baseColumns: PaginatedTableColumn<any>[] = [
      {
        key: "date_created",
        header: "Date Created",
        width: "w-[15%]",
        render: (request) => (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(request.date_created)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(request.date_created)}
            </div>
          </div>
        )
      },
      {
        key: "model",
        header: "Model",
        width: "w-[15%]",
        render: (request) => (
          <div className="text-sm font-gc-reg text-foreground">
            {request.params?.model ? (
              request.params.model.length > 16 
                ? `...${request.params.model.slice(-16)}`
                : request.params.model
            ) : "-"}
          </div>
        )
      },
      {
        key: "tags",
        header: (
          <div className="flex items-center gap-1">
            Tags
            <AiStars className="w-3 h-3 text-muted-foreground" />
          </div>
        ),
        width: "w-[20%]",
        render: (request) => (
          <div className="flex flex-wrap gap-1">
            {(() => {
              try {
                const metaData = JSON.parse(
                  (request.metadata?._maitai_request_meta as string) || "{}"
                );
                const tags = metaData.tags || [];
                const displayTags = tags.slice(0, 2);
                const hasMoreTags = tags.length > 2;
                
                return (
                  <>
                    {displayTags.map((tag: string) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        {tag}
                      </span>
                    ))}
                    {hasMoreTags && (
                      <span className="text-xs text-muted-foreground">
                        +{tags.length - 2} more
                      </span>
                    )}
                    {tags.length === 0 && (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </>
                );
              } catch (e) {
                return <span className="text-sm text-muted-foreground">—</span>;
              }
            })()}
          </div>
        )
      },
      {
        key: "ttft",
        header: "TTFT",
        width: "w-[10%]",
        render: (request) => (
          <div className="text-sm font-gc-reg text-foreground">
            {(() => {
              try {
                const metaData = JSON.parse(
                  (request.metadata?._maitai_request_meta as string) || "{}"
                );
                const ttft = metaData?.first_token_time || 0;
                return ttft > 0 ? `${Math.round(ttft)}ms` : "—";
              } catch (e) {
                return "—";
              }
            })()}
          </div>
        )
      }
    ];

    // Conditionally add Application column
    if (showApplicationColumn) {
      baseColumns.push({
        key: "application",
        header: "Application",
        width: "w-[15%]",
        render: (request) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/application/${request.application_id}`);
            }}
            className="text-sm font-gc-reg text-maitai-green hover:underline"
          >
            {request.application_name || "-"}
          </button>
        )
      });
    }

    // Add Actions column
    baseColumns.push({
      key: "actions",
      header: "", // No header label
      width: "w-[5%]",
      render: (request) => (
        <div className="flex items-center justify-center">
          <div
            className="relative group"
            onMouseEnter={() => setShowOverlay(request.request_id)}
            onMouseLeave={() => setShowOverlay(null)}
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-accent"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SearchIcon className="w-3 h-3 text-muted-foreground" />
            </Button>
            {showOverlay === request.request_id && (
              <div 
                className="absolute z-50 right-0 top-full bg-transparent border-none shadow-none max-w-[800px] w-[800px] pt-1"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <RequestView
                  request={request}
                  apiKey={apiKey}
                  isEditable={false}
                  requestId={request.request_id}
                  applicationId={request.application_id?.toString()}
                  applicationActionId={entityType === "applicationAction" ? entityId.toString() : undefined}
                />
              </div>
            )}
          </div>
        </div>
      )
    });

    // Add remaining columns
    baseColumns.push({
      key: "request_id",
      header: "Request ID",
      width: "w-[15%]",
      render: (request) => (
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
            ...{request.request_id?.slice(-6) || ""}
          </code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(request.request_id || "");
            }}
          >
            <CopyIcon className="w-3 h-3" />
          </Button>
        </div>
      )
    });

    return baseColumns;
  }, [showApplicationColumn, navigate, showOverlay, apiKey, entityType, entityId]);

  return (
    <PaginatedTable
      data={requests}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      pageSize={pageSize}
      totalItems={requestsTotal}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={handleRowClick}
      rowClassName="cursor-pointer hover:bg-accent/20 transition-colors"
      serverSidePagination={true}
    />
  );
};

export default RequestsTable; 